import type { FC } from "react";
import { useCallback, useEffect, useRef } from "react";
import {
    CanvasContainer,
    FlexSection,
    defaultVideoConstraints,
    useCameraKit,
    useCameraKitState,
    useLoginKitState,
    useSetSourceAction,
} from "shared-resources";
import { css } from "@emotion/react";
import { Transform2D, createMediaStreamSource } from "@snap/camera-kit";
import { usePush2Web } from "./usePush2Web";
import { Push2WebPanel } from "./Push2WebPanel";

const imageStyle = css`
    max-width: 100%;
    max-height: 20vh;
    border-radius: 15px;
    margin-bottom: 1em;
`;

const guideStyle = css`
    margin-top: 1em;
`;

export const Push2WebContainer: FC = () => {
    const { cameraKit, cameraKitSession } = useCameraKitState();
    const { stream, setRenderSize } = useCameraKit();
    const setSource = useSetSourceAction();
    const { accessToken } = useLoginKitState();
    const { push2Web, state } = usePush2Web();
    const subscription = useRef<ReturnType<typeof push2Web.subscribe> | undefined>(undefined);

    const subscribe = useCallback(() => {
        if (cameraKitSession && cameraKit && accessToken) {
            subscription.current = push2Web.subscribe(accessToken, cameraKitSession, cameraKit.lensRepository);
        }
    }, [accessToken, cameraKit, cameraKitSession, push2Web]);

    const unsubscribe = () => {
        if (subscription.current) {
            subscription.current.unsubscribe();
            subscription.current = undefined;
        }
    };

    useEffect(() => {
        subscribe();

        return () => {
            unsubscribe();
        };
    }, [subscribe]);

    useEffect(() => {
        if (stream && accessToken) {
            setSource(
                createMediaStreamSource(stream, {
                    transform: Transform2D.MirrorX,
                })
            ).then(() => {
                // TODO: Unify the logic of calling setRenderSize with our default constraints when calling setSource.
                // Else we run the risk of having scenarios where calling setSource might affect css constraints

                // In order to keep this experience the same across devices we manually set the render size to be
                // 640w 480h for all devices. This keeps the canvas contained within parent element's aspect ratio
                setRenderSize(defaultVideoConstraints.width.ideal, defaultVideoConstraints.height.ideal);
            });
        }

        // NOTE: Do not add setRenderSize method to the dependency array at this time. There's an existing bug
        // related to our state updates that triggers unnecessary rerenders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream, accessToken, setSource]);

    return (
        <>
            <div css={guideStyle}>
                <FlexSection>
                    <CanvasContainer canvasOutput={cameraKitSession?.output.live} />
                </FlexSection>
                <Push2WebPanel subscribe={subscribe} unsubscribe={unsubscribe} subscriptionState={state} />
                <br />
                <h3>Setup</h3>
                <p>
                    1. Sign in on this page with your Snapchat account by clicking on the "Continue with Snapchat"
                    button above.
                </p>
                <p>2. In the Lens Studio, sign in to My Lenses with the same account. Then create a new Lens.</p>
                <img css={imageStyle} src="login-to-my-lenses.jpg" />
                <p>3. Make sure Camera Kit is selected in the project settings.</p>
                <img css={imageStyle} src="project-settings.jpg" />
                <p>
                    4. When you are ready to test your Lens, pair your account by clicking on the "Preview Lens" button
                    in the Lens Studio and complete the pairing. Then click the "Send to All Camera Kit" button. Your
                    lens should be visible on this page in a few moments.
                </p>
                <img css={imageStyle} src="send-to-camera-kit.jpg" />
            </div>
        </>
    );
};
